// The drawer's "Your visit" hold chip. Pure: no React, no network, no
// Date.now(); the spec (e2e/visit-checkout-logic.spec.ts) drives it directly.
//
//   * holdChipText: what the "held while you check out" chip under a cart line
//     says. The length comes from the SERVER's hold (get_cart_hold_state's
//     hold_expires_at for that booking, counted on the server's clock), never
//     an assumed 15 minutes: since PR #1433 the server holds a guest for the
//     venue's "Deposit payment window" (tenants.paylink_minutes). While the
//     page does not know the expiry it says no number at all; once the hold
//     has run out it says so (the checkout's own words).
//
// (pairFields, which neighbouring questions of a rider's form share a row,
// now lives in @vertu/shared's booking-core/question-layout.ts, beside the
// rest of the rider card's layout: the admin pay-link page lays out the same
// questions the same way.)

import { LAPSE_MESSAGE } from "./hold-timer-logic";

/** What the page knows about one line's hold. */
export type LineHold =
  /** Not asked yet: the first read has not answered. */
  | { kind: "pending" }
  /** The read failed (older server, offline), or an answer this page cannot read. */
  | { kind: "unknown" }
  /** Pending with a clock: when it lapses, in the server's ms. */
  | { kind: "held"; expiresAt: number }
  /** A managed deposit: pending, but no clock to count. */
  | { kind: "managed" }
  /** The server says the hold's time ran out. */
  | { kind: "lapsed" }
  /** No longer pending (confirmed, cancelled), or not this cart's. */
  | { kind: "ended" };

/**
 * One chip: its words, and how it reads. "pending" keeps the chip's line
 * (the caller draws it invisible) so the text never changes in front of the
 * guest while the first read is on its way.
 */
export type HoldChip = { text: string; tone: "held" | "lapsed" | "pending" };

/**
 * The chip, or null for no chip.
 *   pending                an invisible "Held while you check out" (keeps the line)
 *   unknown / managed      "Held while you check out" (true; no number known)
 *   held, time left        "Held for N min while you check out", N the whole
 *                          minutes left rounded up (29:01 left reads 30)
 *   held, clock ran out    the checkout's lapse line (LAPSE_MESSAGE), so the
 *   lapsed                 step does not go silent the moment it matters
 *   ended                  null (the checkout says what happens next)
 * `serverNow` is the device's clock plus the skew from the last read.
 */
export function holdChipText(hold: LineHold, serverNow: number): HoldChip | null {
  switch (hold.kind) {
    case "pending":
      return { text: "Held while you check out", tone: "pending" };
    case "unknown":
    case "managed":
      return { text: "Held while you check out", tone: "held" };
    case "lapsed":
      return { text: LAPSE_MESSAGE, tone: "lapsed" };
    case "ended":
      return null;
    case "held": {
      const left = hold.expiresAt - serverNow;
      if (Number.isNaN(left)) return null;
      if (left <= 0) return { text: LAPSE_MESSAGE, tone: "lapsed" };
      return { text: `Held for ${Math.max(1, Math.ceil(left / 60_000))} min while you check out`, tone: "held" };
    }
  }
}

/**
 * The soonest moment (server ms) at which one of these chips' words change
 * from a countdown to the lapse line, or null: the caller re-renders then,
 * rather than waiting for its next 30 s tick.
 */
export function nextLapseAt(holds: readonly LineHold[], serverNow: number): number | null {
  let soonest: number | null = null;
  for (const h of holds)
    if (h.kind === "held" && Number.isFinite(h.expiresAt) && h.expiresAt > serverNow)
      soonest = soonest === null ? h.expiresAt : Math.min(soonest, h.expiresAt);
  return soonest;
}

/**
 * get_cart_hold_state's answer (20260927145010), read into one LineHold per
 * asked id, plus the server's clock minus the device's. Anything that is not
 * a well-formed ok answer gives every id "unknown" and no skew: an older
 * server without the function, or a failed call, must never read as a lapse.
 */
export function readHoldState(
  ids: readonly string[],
  data: unknown,
  deviceNow: number,
): { holds: Record<string, LineHold>; skewMs: number } {
  const unknown = { holds: Object.fromEntries(ids.map((id) => [id, { kind: "unknown" } as LineHold])), skewMs: 0 };
  if (!data || typeof data !== "object") return unknown;
  const d = data as { ok?: unknown; now?: unknown; holds?: unknown };
  if (d.ok !== true || !Array.isArray(d.holds)) return unknown;
  const serverNow = typeof d.now === "string" ? Date.parse(d.now) : Number.NaN;
  const skewMs = Number.isFinite(serverNow) ? serverNow - deviceNow : 0;
  const holds: Record<string, LineHold> = {};
  // A named row missing from the answer is another cart's, or has no row:
  // the server answers only for this cart's rows.
  for (const id of ids) holds[id] = { kind: "ended" };
  for (const h of d.holds as unknown[]) {
    if (!h || typeof h !== "object") continue;
    const row = h as { booking_id?: unknown; state?: unknown; hold_expires_at?: unknown };
    if (typeof row.booking_id !== "string" || !(row.booking_id in holds)) continue;
    const at = typeof row.hold_expires_at === "string" ? Date.parse(row.hold_expires_at) : Number.NaN;
    holds[row.booking_id] =
      row.state === "held" && Number.isFinite(at)
        ? { kind: "held", expiresAt: at }
        : row.state === "managed"
          ? { kind: "managed" }
          : row.state === "lapsed"
            ? { kind: "lapsed" }
            : row.state === "gone"
              ? { kind: "ended" }
            : { kind: "unknown" };
  }
  return { holds, skewMs };
}
